import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import me from "../assets/img/me.jpg";
import { getLocalizedExperienceYears } from "../utils/experienceYears";

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.secondary};
  position: relative;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }

  &:after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 4px;
    background-color: ${({ theme }) => theme.colors.primary};

    @media (max-width: 768px) {
      width: 80px;
      bottom: -8px;
    }
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProfileSection = styled(motion.div)`
  /* Eliminamos el estilo anterior */
`;

const ProfileImage = styled.div`
  width: 100%;
  max-width: 350px;
  margin: 0 auto;
  height: 0;
  padding-bottom: 100%; /* Proporción cuadrada */
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    max-width: 250px;
    padding-bottom: 250px; /* Tamaño fijo en vez de porcentaje para pantallas pequeñas */
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover; /* Esto es clave para que la imagen se ajuste bien */
    object-position: center top; /* Ajustar para centrar mejor el rostro */
    transition: transform 0.5s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
`;

const ProfileName = styled.h2`
  text-align: center;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-top: 1rem;
  }
`;

const ProfileTitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 1.2rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ProfileContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 10%;
    width: 80%;
    height: 5px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.secondary}
    );
    border-radius: 10px;
  }
`;

const DescriptionSection = styled(motion.div)`
  p {
    margin-bottom: 1.5rem;
    line-height: 1.8;
  }

  @media (max-width: 768px) {
    p {
      font-size: 0.95rem;
      margin-bottom: 1.2rem;
      text-align: justify;
    }
  }
`;

const SectionDivider = styled.div`
  height: 2px;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary}22,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.primary}22
  );
  margin: 4rem 0 3rem;

  @media (max-width: 768px) {
    margin: 2.5rem 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  text-align: center;
  position: relative;
  color: black;

  &:after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.2rem;

    &:after {
      width: 60px;
    }
  }
`;

const TimelineSection = styled.section`
  /* En pantallas grandes, cada sección ocupa su propia columna */
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: rgb(255, 255, 255);
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    margin-top: 2rem;
    padding: 1.2rem;

    /* Eliminar el margen superior de la primera sección en móvil */
    &:first-child {
      margin-top: 0;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  margin-bottom: 2rem;
  padding-left: 2rem;
  position: relative;

  p {
    line-height: 1.6;
    color: #333;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    p {
      font-size: 0.9rem;
      margin-bottom: 0;
    }
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.5rem;
    width: 1rem;
    height: 1rem;
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;

    @media (max-width: 768px) {
      width: 0.8rem;
      height: 0.8rem;
      top: 0.4rem;
    }
  }

  &:after {
    content: "";
    position: absolute;
    left: 0.5rem;
    top: 1.5rem;
    width: 1px;
    height: calc(100% + 1rem);
    background-color: ${({ theme }) => theme.colors.textLight};

    @media (max-width: 768px) {
      left: 0.4rem;
    }
  }

  &:last-child:after {
    display: none;
  }
`;

const TimelineDate = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  display: block;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.3rem;
  }
`;

const TimelineTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: black;
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.4rem;
  }
`;

const TimelineSubtitle = styled.h4`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 1rem;
  color: black;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.7rem;
  }
`;

const TimelineSectionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr; /* Por defecto dos columnas en pantallas grandes */
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Una columna en pantallas pequeñas */
    gap: 1.5rem;
  }
`;

const pageVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8 },
  },
};

const AboutPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  // Desplazamiento a la parte superior de la página
  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      import("../utils/secureStorage").then(({ getSecureStorage }) => {
        const secureData = getSecureStorage();
        const token = secureData.token;
        if (token) {
          setIsLoggedIn(true);
        }
      });
    } catch (e) {}
  }, []);

  // Traducción de los datos
  const { t, i18n } = useTranslation();
  const experienceYears = getLocalizedExperienceYears(i18n.language, 2019);

  // Datos de educación y referencias
  const education = t("about.education_items", {
    returnObjects: true,
  }) as any[];
  const references = t("about.references_items", {
    returnObjects: true,
  }) as any[];

  const educationWithoutNoLogin = education.map((item: any) => {
    return {
      ...item,
      date: "XXXX",
      title: "XXXX XXXX XXXX",
      subtitle: "XXXX XXXX XXXX",
      description:
        "XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX",
    };
  });

  const referencesWithoutNoLogin = references.map((item: any) => {
    return {
      ...item,
      name: "XXXX XXXX XXXX",
      title: "XXXX XXXX XXXX",
      contact: "XXXX XXXX XXXX",
    };
  });

  return (
    <PageContainer
      as={motion.div}
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      <PageTitle variants={itemVariants}>{t("about.title")}</PageTitle>

      <AboutContent>
        <ProfileSection variants={itemVariants}>
          <ProfileContainer>
            <ProfileImage>
              <img src={me} alt="Profile" />
            </ProfileImage>
          </ProfileContainer>
          <ProfileName>{t("about.profile_name")}</ProfileName>
          <ProfileTitle>{t("about.profile_title")}</ProfileTitle>
        </ProfileSection>

        <DescriptionSection variants={itemVariants}>
          <p>{t("about.bio.paragraph1", { experienceYears })}</p>
          <p>{t("about.bio.paragraph2")}</p>
          <p>{t("about.bio.paragraph3")}</p>
        </DescriptionSection>
      </AboutContent>

      <SectionDivider />

      <TimelineSectionsContainer>
        <TimelineSection>
          <SectionTitle variants={itemVariants}>
            {t("about.education")}
          </SectionTitle>
          {isLoggedIn
            ? education.map((item: any, index: number) => (
                <TimelineItem key={index} variants={itemVariants}>
                  <TimelineDate>{item.date}</TimelineDate>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineSubtitle>{item.subtitle}</TimelineSubtitle>
                  <p>{item.description}</p>
                </TimelineItem>
              ))
            : educationWithoutNoLogin.map((item: any, index: number) => (
                <TimelineItem
                  variants={itemVariants}
                  className="blur-effect"
                  onClick={() => navigate("/login")}
                >
                  <TimelineDate>{item.date}</TimelineDate>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineSubtitle>{item.subtitle}</TimelineSubtitle>
                  <p>{item.description}</p>
                </TimelineItem>
              ))}
        </TimelineSection>

        <TimelineSection>
          <SectionTitle variants={itemVariants}>
            {t("about.references")}
          </SectionTitle>

          {isLoggedIn
            ? references.map((ref: any, index: number) => (
                <TimelineItem key={index} variants={itemVariants}>
                  <TimelineTitle>{ref.name}</TimelineTitle>
                  <TimelineSubtitle>{ref.title}</TimelineSubtitle>
                  <p>
                    {t("about.contact_label")}: {ref.contact}
                  </p>
                </TimelineItem>
              ))
            : referencesWithoutNoLogin.map((item: any, index: number) => (
                <TimelineItem key={index} variants={itemVariants} className="blur-effect" onClick={() => navigate("/login")}>
                  <TimelineTitle>{item.name}</TimelineTitle>
                  <TimelineSubtitle>{item.title}</TimelineSubtitle>
                  <p>{item.contact}</p>
                </TimelineItem>
              ))}
        </TimelineSection>
      </TimelineSectionsContainer>
    </PageContainer>
  );
};

export default AboutPage;
